import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { useUpdateOnboarding } from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';
import {
  Button,
  ChannelIcon,
  type ChannelType,
  Field,
  Input,
  Select,
  Textarea,
  channelLabel,
} from '../components/ui/index.js';

const STEPS = [
  { key: 'account', title: 'Аккаунт', desc: 'Расскажи о компании' },
  { key: 'channels', title: 'Каналы', desc: 'Выбери первые подключения' },
  { key: 'widget', title: 'Виджет', desc: 'Установи на сайт' },
  { key: 'operators', title: 'Операторы', desc: 'Пригласи команду' },
  { key: 'done', title: 'Готово', desc: 'Запуск!' },
] as const;

const CHANNELS: ChannelType[] = ['chat', 'telegram', 'whatsapp', 'email', 'vk', 'wildberries'];

export function OnboardingPage(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const updateOnboarding = useUpdateOnboarding(user?.workspaceSlug);
  const [step, setStep] = useState(0);
  const [selectedChannels, setSelectedChannels] = useState<Set<ChannelType>>(new Set(['chat']));

  function advanceStep(): void {
    const nextStep = step + 1;
    void updateOnboarding.mutate({
      step: STEPS[nextStep]?.key ?? 'done',
      completed_steps: STEPS.slice(0, nextStep).map((s) => s.key),
    });
    setStep(nextStep);
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '240px 1fr',
        height: '100vh',
        background: 'var(--inbx-bg)',
      }}
    >
      <aside style={{ borderRight: '1px solid var(--inbx-border)', padding: 20 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: 'var(--inbx-accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 700,
            marginBottom: 20,
          }}
        >
          I
        </div>
        {STEPS.map((s, i) => (
          <div
            key={s.key}
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              padding: '8px 0',
              opacity: i < step ? 0.6 : 1,
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: 11,
                background: i <= step ? 'var(--inbx-accent)' : 'var(--inbx-bg-secondary)',
                color: i <= step ? '#fff' : 'var(--inbx-text-tertiary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {i < step ? <i className="ti-check" /> : i + 1}
            </div>
            <div>
              <div style={{ fontWeight: i === step ? 600 : 400, fontSize: 'var(--inbx-fz-md)' }}>
                {s.title}
              </div>
              <div style={{ fontSize: 'var(--inbx-fz-xs)', color: 'var(--inbx-text-tertiary)' }}>
                {s.desc}
              </div>
            </div>
          </div>
        ))}
      </aside>

      <main style={{ overflow: 'auto', padding: 40 }}>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <div
            style={{
              height: 4,
              background: 'var(--inbx-bg-secondary)',
              borderRadius: 2,
              marginBottom: 32,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${(step / (STEPS.length - 1)) * 100}%`,
                background: 'var(--inbx-accent)',
                borderRadius: 2,
                transition: 'width 0.2s',
              }}
            />
          </div>

          {step === 0 && (
            <>
              <h1 style={{ marginTop: 0 }}>Расскажите о вашей компании</h1>
              <p style={{ color: 'var(--inbx-text-tertiary)' }}>
                Это поможет настроить сервис под ваши нужды
              </p>
              <Field label="Название компании" required>
                <Input placeholder="Demo Co." />
              </Field>
              <Field label="Сфера деятельности" required>
                <Select>
                  <option>e-commerce</option>
                  <option>SaaS</option>
                  <option>финансы</option>
                  <option>образование</option>
                  <option>услуги</option>
                  <option>другое</option>
                </Select>
              </Field>
              <Field label="Ожидаемый объём в месяц" required>
                <Select>
                  <option>до 500</option>
                  <option>500–5000</option>
                  <option>5000–20000</option>
                  <option>более 20000</option>
                </Select>
              </Field>
              <Field label="Часовой пояс" required>
                <Select defaultValue="Europe/Moscow">
                  <option>Europe/Moscow</option>
                  <option>Asia/Yekaterinburg</option>
                </Select>
              </Field>
            </>
          )}

          {step === 1 && (
            <>
              <h1 style={{ marginTop: 0 }}>Выберите первые каналы</h1>
              <p style={{ color: 'var(--inbx-text-tertiary)' }}>Можно добавить ещё позже</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {CHANNELS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      const next = new Set(selectedChannels);
                      if (next.has(c)) next.delete(c);
                      else next.add(c);
                      setSelectedChannels(next);
                    }}
                    style={{
                      padding: 16,
                      border: selectedChannels.has(c)
                        ? '2px solid var(--inbx-accent)'
                        : '1px solid var(--inbx-border)',
                      borderRadius: 8,
                      background: selectedChannels.has(c)
                        ? 'var(--inbx-accent-soft)'
                        : 'var(--inbx-bg)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <ChannelIcon channel={c} size={36} />
                    <span style={{ fontSize: 'var(--inbx-fz-sm)', fontWeight: 500 }}>
                      {channelLabel(c)}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h1 style={{ marginTop: 0 }}>Вставьте виджет на сайт</h1>
              <p style={{ color: 'var(--inbx-text-tertiary)' }}>
                Скопируйте и вставьте перед <code>&lt;/body&gt;</code>
              </p>
              <pre
                style={{
                  background: 'var(--inbx-bg-secondary)',
                  padding: 14,
                  borderRadius: 'var(--inbx-radius)',
                  fontFamily: 'var(--inbx-font-mono)',
                  fontSize: 'var(--inbx-fz-sm)',
                  overflow: 'auto',
                }}
              >
                {`<script async src="https://inbx.app/widget.js"
        data-widget="wk_demo123"></script>`}
              </pre>
              <Button variant="secondary" icon="ti-copy">
                Копировать
              </Button>
              <div
                style={{
                  marginTop: 16,
                  padding: 14,
                  background: 'var(--inbx-bg-secondary)',
                  borderRadius: 'var(--inbx-radius)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <i className="ti-loader-2" /> Ожидаем первый тикет...
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h1 style={{ marginTop: 0 }}>Пригласите операторов</h1>
              <Field label="Email операторов (через запятую или с новой строки)">
                <Textarea rows={3} placeholder="alex@team.co, maria@team.co" />
              </Field>
              <Field label="Роль по умолчанию">
                <Select>
                  <option>Operator</option>
                  <option>Supervisor</option>
                  <option>Admin</option>
                </Select>
              </Field>
              <Button variant="secondary">Пропустить</Button>
            </>
          )}

          {step === 4 && (
            <>
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 32,
                    background: 'var(--inbx-success-bg)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16,
                  }}
                >
                  <i
                    className="ti-circle-check"
                    style={{ fontSize: 32, color: 'var(--inbx-success)' }}
                  />
                </div>
                <h1 style={{ margin: 0 }}>Всё готово!</h1>
                <p style={{ color: 'var(--inbx-text-tertiary)' }}>
                  Workspace настроен — можно начинать
                </p>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 10,
                  marginBottom: 24,
                }}
              >
                <QuickLink
                  icon="ti-robot"
                  label="Создать бот-сценарий"
                  onClick={() => navigate({ to: '/bots' })}
                />
                <QuickLink
                  icon="ti-clock"
                  label="Настроить SLA"
                  onClick={() => navigate({ to: '/settings/sla' })}
                />
                <QuickLink
                  icon="ti-database"
                  label="Подключить данные"
                  onClick={() => navigate({ to: '/settings/data-connectors' })}
                />
                <QuickLink
                  icon="ti-device-mobile"
                  label="Мобильное приложение"
                  onClick={() => {}}
                />
              </div>
              <Button variant="primary" size="md" onClick={() => navigate({ to: '/inbox' })}>
                Открыть inbox →
              </Button>
            </>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 32 }}>
            {step > 0 && step < STEPS.length - 1 && (
              <Button variant="secondary" onClick={() => setStep(step - 1)}>
                ← Назад
              </Button>
            )}
            {step < STEPS.length - 1 && (
              <Button variant="primary" onClick={advanceStep} style={{ marginLeft: 'auto' }}>
                {step === STEPS.length - 2 ? 'Завершить' : 'Далее →'}
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function QuickLink({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inbx-card"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 14,
        marginBottom: 0,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <i className={icon} style={{ fontSize: 18, color: 'var(--inbx-accent)' }} />
      <span style={{ fontWeight: 500 }}>{label}</span>
    </button>
  );
}
